<template lang="pug">
div
  v-card.py-4.mt-3.round-xl(v-if="!treatmentItem.delete" flat)
    template(v-if="isFetchingPriceTier || isFetchingPackage")
      div.px-5
        v-skeleton-loader(type="heading")
        v-skeleton-loader.mt-3(type="paragraph")
    template(v-else)
      div.px-5
        div.d-flex.align-center
          v-icon.mr-2(v-if="isPackage" color="neutral700" size="16") $yzi-package
          v-icon.mr-2(v-else-if="isDocument" color="neutral700" size="16") $yzi-file-05
          v-icon.mr-2(v-else-if="isMedicine" color="neutral700" size="16") $yzi-medicine
          v-icon.mr-2(v-else color="neutral700" size="16") $yzi-stethoscope
          div.text-sm.font-semibold.neutral700--text {{ treatmentItem.name }}
          yz-badge.ml-2(v-if="isFromHq" x-small color="blue-100" text-color="blue-500" border) HQ
          template(v-if="isMedicine")
            div.text-xs.neutral500--text.ml-2(v-if="treatmentItem.sku") {{ treatmentItem.sku }}
            yz-badge.ml-2(v-if="treatmentItem.supplier" color="neutral-200" text-color="neutral-700" border border-color="neutral-300") {{ treatmentItem.supplier }}
          template(v-if="inventoryEnabled")
            div.text-xs.neutral500--text.ml-3(v-if="displayUom") UOM: {{ displayUom }}
            template(v-if="isPackage")
              div.text-xs.neutral500--text.ml-3 On hand: {{ inventoryQty != null ? inventoryQty : '-' }}
            template(v-else)
              div.d-flex.align-center.ml-3
                v-icon.mr-1(size="8" color="success500") $yzi-circle
                div.text-xs.neutral500--text Available: {{ availableQty != null ? availableQty : '-' }}
              div.text-xs.neutral500--text.ml-3 On hand: {{ inventoryQty != null ? inventoryQty : '-' }}
          div.clickable.ml-2(v-if="isDocument" @click="openDocument")
            y-chip.no-wrap(v-if="treatmentItem.document && treatmentItem.document.status && treatmentItem.document.status === 'incomplete'" label="Incomplete | Continue" size="xs" outlined)
              template(#prepend)
                v-avatar.mr-1(size="6" color="danger500")
              template(#append)
                v-icon.ml-1(size="12" color="neutral700") $yzi-arrow-up-right
            y-chip.no-wrap(v-else label="Completed | Edit" size="xs" outlined)
              template(#prepend)
                v-avatar.mr-1(size="6" color="info500")
              template(#append)
                v-icon.ml-1(size="12" color="neutral700") $yzi-arrow-up-right
          v-spacer
          div.clickable.mr-5(v-if="isMedicine" @click="treatmentItem.activeMedication = !treatmentItem.activeMedication")
            y-chip.no-wrap(label="Set as active medicine" size="xs" outlined )
              template(#prepend)
                v-icon.mr-1(v-if="treatmentItem.activeMedication" size="20" color="success500") $yzi-check-circle-solid
                v-icon.mr-1(v-else size="20" color="neutral500") mdi-radiobox-blank
          y-button.mr-2(outlined color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-copy-03" icon-color="neutral700" icon-size="16" unset-min-width size="xxs" height="24" button-class="px-1" @click="duplicateItem")
          y-button(outlined color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="danger500" icon-size="16" unset-min-width size="xxs" height="24" button-class="px-1" @click="deleteItem")

        div.mt-3
          v-row(dense)
            v-col(:style="hasUomConversion || treatmentItem.uom ? 'max-width:150px;' : 'max-width:70px;'")
              div.text-xs.font-medium.neutral500--text Quantity
              div.d-flex.mt-1(style="gap: 4px;")
                v-text-field.text-xs.round-md.custom-height(ref="quantityField" v-model="treatmentItem.quantity" outlined placeholder="0" type="number" :rules="inventoryEnabled ? allowedQuantityRules : rules.has_quantity" hide-details dense :style="hasUomConversion ? 'max-width: 75px;' : 'max-width: 60px;'")
                  template(v-if="hasUomConversion && uomConversionEquivalents.length > 0" v-slot:append)
                    v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                      template(v-slot:activator="{ on, attrs }")
                        v-icon(color="neutral500" size="14" v-bind="attrs" v-on="on") mdi-information-outline
                      div.py-1
                        div.text-xs.font-semibold.white--text Equivalent to
                        ul.pl-4.mt-1
                          li.text-xs.white--text(v-for="(equiv, idx) in uomConversionEquivalents" :key="idx") {{ equiv }}
                medication-unit-of-measure-dropdown(v-if="hasUomConversion" v-model="treatmentItem.selectedUom" :allowedValues="uomOptions" :fieldMinWidth="50" fieldStyle="max-width: 70px;" size="md" placeholder="UOM" :deletable="false")
                v-text-field.text-xs.round-md.custom-height(v-else-if="treatmentItem.uom" :value="treatmentItem.uom" outlined dense hide-details readonly disabled style="background-color: var(--v-neutral300); max-width: 80px;")
            v-col(v-if="hasPriceTier" style="max-width:160px;")
              div.text-xs.font-medium.neutral500--text Price tier
              treatment-price-tier-dropdown.mt-1(v-model="treatmentItem.priceTier" :item="treatmentItem" size="xs" @change="updateRate($event)")
            v-col(style="max-width:92px;")
              div.text-xs.font-medium.neutral500--text Rate
              v-text-field.text-xs.round-md.custom-height.mt-1(v-model="treatmentItem.rate" outlined placeholder="0" type="number" :rules="rules.must_positive_value" hide-details dense :style="!allowedPriceEdit ? 'background-color: var(--v-neutral300)' : ''" :disabled="!allowedPriceEdit" :prefix="$currency.symbol")
            v-col(style="max-width:100px;")
              div.text-xs.font-medium.neutral500--text Amount
              v-text-field.text-xs.round-md.custom-height.mt-1(v-model="amount" outlined placeholder="0" type="number" hide-details dense disabled style="background-color: var(--v-neutral300)" :prefix="$currency.symbol")
            template(v-if="isMedicine" dense)
              v-col
                div.text-xs.font-medium.neutral500--text Indication
                treatment-indication-dropdown.mt-1(v-model="treatmentItem.indication" size="xs")
              v-col(style="min-width:160px;")
                div.text-xs.font-medium.neutral500--text Dosage
                v-row(dense)
                  v-col(style="max-width:55px;")
                    v-text-field.text-xs.round-md.custom-height.mt-1(v-model="treatmentItem.dosageValue" outlined placeholder="0" type="number" hide-details dense)
                  v-col
                    treatment-dosage-unit-dropdown.mt-1(v-model="treatmentItem.dosageUnit" size="xs")
              v-col
                div.text-xs.font-medium.neutral500--text Frequency
                treatment-frequency-dropdown.mt-1(v-model="treatmentItem.frequency" size="xs")
              v-col
                div.text-xs.font-medium.neutral500--text Instruction
                treatment-instruction-dropdown.mt-1(v-model="treatmentItem.instruction" size="xs")
              v-col
                div.text-xs.font-medium.neutral500--text Duration
                treatment-duration-dropdown.mt-1(v-model="treatmentItem.duration" size="xs")
              v-col
                div.text-xs.font-medium.neutral500--text Precaution
                treatment-precaution-dropdown.mt-1(v-model="treatmentItem.precaution" size="xs")
          div.text-xs.error--text.mt-1(v-if="!allowOutOfStock && inventoryEnabled && treatmentItem.quantity > (fromReservationPreorder ? availableQty : inventoryQty)") {{ fromReservationPreorder ? 'Quantity more than available stock' : 'Quantity more than stock on hand' }}
          yz-card.mt-2.pa-2(v-if="!allowOutOfStock && showAvailableQtyWarning" outlined color="yellow-100" style="max-width: 800px;")
            div.d-flex.align-center
              v-icon.mr-2(size="14" color="danger") $yzi-alert-circle-solid
              div.text-xs.flex-grow-1 Dispensing this quantity will use the reserved &amp; pre-order stock. Override and allow add excess quantity as To Order?
              yz-checkbox.ml-2.flex-shrink-0(v-model="acknowledgedExcessQty")
      //- package child
      template(v-if="isPackage")
        template(v-for="(treatment, idx) in treatmentItem.bundle.treatments")
          div(v-if="!treatment.delete" :key="`pckgitm-${idx}`")
            v-divider.neutral300.my-3
            div.px-5(:key="`trpcg-${idx}`")
              div.d-flex.align-center
                v-icon.mx-2.mb-1(size="16" color="neutral700" ) $yzi-corner-down-right
                div.text-sm.font-semibold.neutral700--text {{ treatment.name }}
                yz-badge.ml-2(v-if="treatment.branch_for" x-small color="blue-100" text-color="blue-500" border) HQ
                template(v-if="checkMedicine(treatment)")
                  div.text-xs.neutral500--text.ml-2(v-if="treatment.sku") {{ treatment.sku }}
                  yz-badge.ml-2(v-if="treatment.supplier" color="neutral-200" text-color="neutral-700" border border-color="neutral-300") {{ treatment.supplier }}
                template(v-if="treatment.inventory_enabled")
                  div.text-xs.neutral500--text.ml-3(v-if="getChildDisplayUom(treatment)") UOM: {{ getChildDisplayUom(treatment) }}
                  div.d-flex.align-center.ml-3
                    v-icon.mr-1(size="8" color="success500") $yzi-circle
                    div.text-xs.neutral500--text Available: {{ treatment.available_qty != null ? treatment.available_qty : '-' }}
                  div.text-xs.neutral500--text.ml-3 On hand: {{ treatment.inventory_qty != null ? treatment.inventory_qty : '-' }}
                v-spacer
                div.clickable.mr-5(v-if="checkMedicine(treatment)" @click="treatment.activeMedication = !treatment.activeMedication")
                  y-chip.no-wrap(label="Set as active medicine" size="xs" outlined )
                    template(#prepend)
                      v-icon.mr-1(v-if="treatment.activeMedication" size="20" color="success500") $yzi-check-circle-solid
                      v-icon.mr-1(v-else size="20" color="neutral500") mdi-radiobox-blank
                //- y-button(outlined color="neutral400" background-color="var(--v-neutral100)" prepend-icon="$yzi-trash-03" icon-color="danger500" icon-size="16" unset-min-width size="xxs" height="24" button-class="px-1" @click="deleteChildItem(treatment)")

              div.mt-3
                v-row(dense)
                  v-col(:style="childHasUomConversion(treatment) || treatment.uom ? 'max-width:150px;' : 'max-width:70px;'")
                    div.text-xs.font-medium.neutral500--text Quantity
                    div.d-flex.mt-1(style="gap: 4px;")
                      v-text-field.text-xs.round-md.custom-height(v-model="treatment.quantity_per_set" outlined placeholder="0" type="number" :rules="treatment.inventory_enabled ? packageChildrenRules(treatment) : rules.has_quantity" disabled :style="childHasUomConversion(treatment) ? 'max-width: 75px; background-color: var(--v-neutral300)' : 'max-width: 60px; background-color: var(--v-neutral300)'" hide-details dense)
                        template(v-if="childHasUomConversion(treatment) && getChildUomConversionEquivalents(treatment).length > 0" v-slot:append)
                          v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                            template(v-slot:activator="{ on, attrs }")
                              v-icon.mt-n1(color="neutral500" size="14" v-bind="attrs" v-on="on") mdi-information-outline
                            div.py-1
                              div.text-xs.font-semibold.white--text Equivalent to
                              ul.pl-4.mt-1
                                li.text-xs.white--text(v-for="(equiv, idx) in getChildUomConversionEquivalents(treatment)" :key="idx") {{ equiv }}
                      medication-unit-of-measure-dropdown(v-if="childHasUomConversion(treatment)" v-model="treatment.selectedUom" :allowedValues="getChildUomOptions(treatment)" size="md" :fieldMinWidth="70" fieldStyle="max-width: 80px;" placeholder="UOM" :deletable="false")
                      v-text-field.text-xs.round-md.custom-height(v-else-if="treatment.uom" :value="treatment.uom" outlined dense hide-details readonly disabled style="background-color: var(--v-neutral300); max-width: 70px;")
                    div.text-xs.error--text.mt-1(v-if="!allowOutOfStock && treatment.inventory_enabled && treatment.quantity_per_set > getChildInventoryQty(treatment)") Quantity more than available stock
                  template(v-if="checkMedicine(treatment)" dense)
                    v-col
                      div.text-xs.font-medium.neutral500--text Indication
                      treatment-indication-dropdown.mt-1(v-model="treatment.indication" size="xs")
                    v-col
                      div.text-xs.font-medium.neutral500--text Dosage
                      v-row(dense)
                        v-col
                          v-text-field.text-xs.round-md.custom-height.mt-1(v-model="treatment.dosageValue" outlined placeholder="0" type="number" hide-details dense)
                        v-col(cols="8")
                          treatment-dosage-unit-dropdown.mt-1(v-model="treatment.dosageUnit" size="xs")
                    v-col
                      div.text-xs.font-medium.neutral500--text Frequency
                      treatment-frequency-dropdown.mt-1(v-model="treatment.frequency" size="xs")
                    v-col
                      div.text-xs.font-medium.neutral500--text Instruction
                      treatment-instruction-dropdown.mt-1(v-model="treatment.instruction" size="xs")
                    v-col
                      div.text-xs.font-medium.neutral500--text Duration
                      treatment-duration-dropdown.mt-1(v-model="treatment.duration" size="xs")
                    v-col
                      div.text-xs.font-medium.neutral500--text Precaution
                      treatment-precaution-dropdown.mt-1(v-model="treatment.precaution" size="xs")

  .dialogs
    add-medical-document-dialog(v-if="showAddMedicalDocumentDialog" :open.sync="showAddMedicalDocumentDialog" :treatment="treatmentItem" :diagnoses="consultation.diagnoses" :new-document="!treatmentItem.edit" @updated="updateDocument")
    edit-not-allowed-dialog(v-if="showEditNotAllowedDialog" :open.sync="showEditNotAllowedDialog" )
    treatment-plan-uom-mismatch-dialog(v-if="showUomMismatchDialog" :open.sync="showUomMismatchDialog" :inventoryUom="treatmentItem.inventory_uom" :stockUom="treatmentItem.stock ? treatmentItem.stock.uom : null" @confirm="onConfirmUomMismatch" @cancel="onCancelUomMismatch")
    skip-inventory-return-dialog(v-if="showSkipInventoryDialog" :open.sync="showSkipInventoryDialog" @confirm="onConfirmSkipInventory" @cancel="onCancelSkipInventory")
    uom-reselection-dialog(v-if="showUomReselectionDialog" :open.sync="showUomReselectionDialog" :allowedValues="uomOptions" :previousUom="treatmentItem.selectedUom" @confirm="onConfirmUomReselection" @cancel="onCancelUomReselection")

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { buildUomGraph, getUomScale as getUomScaleFromGraph } from '@/utils'
import AddMedicalDocumentDialog from '@/components/clinic/consultation/AddMedicalDocumentDialog.vue'
import TreatmentPriceTierDropdown from '@/components/clinic/consultation/TreatmentPriceTierDropdown.vue'
import TreatmentDosageUnitDropdown from '@/components/clinic/consultation/TreatmentDosageUnitDropdown.vue'
import TreatmentFrequencyDropdown from '@/components/clinic/consultation/TreatmentFrequencyDropdown.vue'
import TreatmentIndicationDropdown from '@/components/clinic/consultation/TreatmentIndicationDropdown.vue'
import TreatmentInstructionDropdown from '@/components/clinic/consultation/TreatmentInstructionDropdown.vue'
import TreatmentDurationDropdown from '@/components/clinic/consultation/TreatmentDurationDropdown.vue'
import TreatmentPrecautionDropdown from '@/components/clinic/consultation/TreatmentPrecautionDropdown.vue'
import EditNotAllowedDialog from '@/components/clinic/consultation/EditNotAllowedDialog.vue'
import TreatmentPlanUomMismatchDialog from '@/components/clinic/consultation/TreatmentPlanUomMismatchDialog.vue'
import SkipInventoryReturnDialog from '@/components/clinic/consultation/SkipInventoryReturnDialog.vue'
import UomReselectionDialog from '@/components/clinic/consultation/UomReselectionDialog.vue'
import MedicationUnitOfMeasureDropdown from '@/components/clinic/inventory/MedicationUnitOfMeasureDropdown.vue'

export default {
  components: {
    AddMedicalDocumentDialog, TreatmentPriceTierDropdown, TreatmentDosageUnitDropdown, TreatmentFrequencyDropdown,
    TreatmentIndicationDropdown, TreatmentInstructionDropdown, TreatmentDurationDropdown, TreatmentPrecautionDropdown,
    EditNotAllowedDialog, TreatmentPlanUomMismatchDialog, SkipInventoryReturnDialog, UomReselectionDialog, MedicationUnitOfMeasureDropdown
  },
  props: {
    treatment: {},
    allowOutOfStock: {
      type: Boolean,
      default: false
    },
    fromReservationPreorder: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isFetchingPriceTier: false,
      isFetchingPackage: false,
      isLoading: false,
      showAddMedicalDocumentDialog: false,
      showEditNotAllowedDialog: false,
      showUomMismatchDialog: false,
      showSkipInventoryDialog: false,
      showUomReselectionDialog: false,
      pendingChange: null,
      isRevertingChange: false,
      initialQuantity: 0,
      acknowledgedExcessQty: false,
      rules: {
        has_quantity: [v => !!v || 'This field is required', v => v > 0 || 'This field is required'],
        must_positive_value: [v => (v != null && v.toString().trim().length) || 'This field is required', v => v >= 0 || 'This field must have positive value'],
      },
    }
  },
  computed: {
    ...mapState('user', ['member']),
    ...mapGetters({
      company: 'getCompany',
      consultation: 'treatment/getConsultation',
      registration: 'treatment/getRegistration',
      isDoctor: 'user/isDoctor',
      hqSelectedCompany: 'hq/getSelectedCompany',
      isHq: 'hq/getIsHq'
    }),
    treatmentItem: {
      get () {
        return this.treatment
      },
      set (val) {
        this.$emit('update:treatment', val)
      }
    },
    hasPriceTier () {
      if (this.treatmentItem.type === 'documents' && !this.treatmentItem.document_template.price_tier_enabled) { return false }
      if (this.treatmentItem.type === 'bundle') { return false }
      return true
    },
    amount () {
      return Number(this.treatmentItem.quantity || 0) * Number(this.treatmentItem.rate || 0)
    },
    inventoryEnabled () {
      if (this.isPackage) {
        return Boolean(this.treatmentItem.bundle.available_qty !== null)
      }
      return this.treatmentItem.inventory_enabled
    },
    allowedQuantityRules () {
      // `allowOutOfStock` is set to true by the parent in the preorder flow only —
      // preorders may legitimately request more than current on-hand stock, so we
      // skip the upper-bound cap and the excess-qty acknowledgement entirely.
      // Reservations still go through the full inventory checks below.
      if (this.allowOutOfStock) return [v => !!v || 'This field is required', v => v != 0 || 'This field is required']
      // When editing from the reservation flow, cap at `available_qty` (on-hand
      // minus already committed/reserved/preordered) rather than `inventory_qty`
      // — a reservation can only legitimately hold what's still free to commit.
      // The excess-qty warning is suppressed in this flow, so availableQty is
      // the hard ceiling here.
      const maxQty = this.fromReservationPreorder ? this.availableQty : this.inventoryQty
      const maxQtyLabel = this.fromReservationPreorder ? 'Item quantity more than available stock' : 'Item quantity more than stock on hand'
      return [v => !!v || 'This field is required', v => v != 0 || 'This field is required', v => Number(v) <= maxQty || maxQtyLabel, () => !this.showAvailableQtyWarning || this.acknowledgedExcessQty || 'Please acknowledge the excess quantity warning']
    },
    inventoryQty () {
      if (this.isPackage) {
        let qty = this.treatmentItem.bundle.available_qty
        if (qty == null) return qty
        // When reopening a saved package, bundle.available_qty already excludes
        // this item's own committed quantity. Add initialQuantity back so the
        // existing saved quantity is still considered valid.
        if (this.treatmentItem.item_id) {
          qty = Number(qty) + Number(this.initialQuantity)
        }
        return qty
      }
      if (this.hasUomConversion && this.treatmentItem.selectedUom) {
        let qty = Number(this.treatmentItem.inventory_qty)
        if (this.treatmentItem.item_id && this.treatmentItem.stock) {
          qty += Math.abs(this.treatmentItem.stock.change)
        }
        const scale = this.getUomScale(this.treatmentItem.selectedUom)
        if (scale > 1) {
          qty = qty / scale
        }
        // Reservation items have no `stock` ledger entry yet (stock is only
        // created on dispense), so fall back to `initialQuantity` — already in
        // selectedUom, so add after the scale conversion.
        if (this.treatmentItem.item_id && !this.treatmentItem.stock) {
          qty += Number(this.initialQuantity)
        }
        return qty
      }
      if (this.treatmentItem.item_id) {
        return Number(this.treatmentItem.inventory_qty) + Number(this.initialQuantity)
      }
      return this.treatmentItem.inventory_qty
    },
    availableQty () {
      if (this.isPackage) {
        let qty = this.treatmentItem.bundle.available_qty
        if (qty == null) return qty
        if (this.treatmentItem.item_id) {
          qty = Number(qty) + Number(this.initialQuantity)
        }
        return qty
      }
      if (this.hasUomConversion && this.treatmentItem.selectedUom) {
        let qty = Number(this.treatmentItem.available_qty)
        if (this.treatmentItem.item_id && this.treatmentItem.stock) {
          qty += Math.abs(this.treatmentItem.stock.change)
        }
        const scale = this.getUomScale(this.treatmentItem.selectedUom)
        if (scale > 1) {
          qty = qty / scale
        }
        // Reservation items have no `stock` ledger entry yet (stock is only
        // created on dispense), so fall back to `initialQuantity` — already in
        // selectedUom, so add after the scale conversion.
        if (this.treatmentItem.item_id && !this.treatmentItem.stock) {
          qty += Number(this.initialQuantity)
        }
        return qty
      }
      if (this.treatmentItem.item_id) {
        return Number(this.treatmentItem.available_qty) + Number(this.initialQuantity)
      }
      return this.treatmentItem.available_qty
    },
    showAvailableQtyWarning () {
      // Skip the excess-qty warning when editing from the reservation/preorder flow —
      // the user is already operating within that context, so warning them about
      // consuming reserved/preorder stock would be redundant. See sibling change in
      // TreatmentPlanDialog (commit 668a1fbe9) which hides the reservation count card
      // for the same reason.
      if (this.fromReservationPreorder) return false
      if (!this.inventoryEnabled) return false
      if (this.availableQty == null) return false
      const qty = Number(this.treatmentItem.quantity)
      return qty > this.availableQty && qty <= this.inventoryQty
    },
    allowedPriceEdit () {
      // admin can edit price
      if (this.isAdmin) return true
      // 'lock_treatment_plan' is for CA
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan') && !this.isAdminOrDoctor) return false
      // 'lock_treatment_plan_price_edit' is for doctor
      if (this.company.genes.find(gene => gene.feature === 'lock_treatment_plan_price_edit') && this.isDoctor) return false
      return true
    },
    allowDocumentEdit () {
      if (this.company.genes.find(gene => gene.feature === 'lock_document_edit_treatment_plan') && !this.isAdminOrDoctor) return false
      return true
    },
    isAdmin () {
      return this.member.roles.includes('Admin')
    },
    isAdminOrDoctor () {
      if (this.isAdmin || this.isDoctor) return true
      return false
    },
    isMedicine () {
      if (this.treatmentItem.medication && this.treatmentItem.medication.id) {
        return true
      }
      return false
    },
    isDocument () {
      if (this.treatmentItem.type === 'documents') {
        return true
      }
      return false
    },
    isPackage () {
      if (this.treatmentItem.type === 'bundle') {
        return true
      }
      return false
    },
    isFromHq () {
      return Boolean(this.treatmentItem.branch_for)
    },
    displayUom () {
      if (this.hasUomConversion) {
        return this.treatmentItem.uom_conversions[this.treatmentItem.uom_conversions.length - 1].convert_to
      }
      return this.treatmentItem.uom
    },
    hasUomConversion () {
      return this.treatmentItem.uom_conversion_enabled && this.treatmentItem.uom_conversions && this.treatmentItem.uom_conversions.length > 0
    },
    // When the product's current base UOM (inventory_uom) differs from the stock record's UOM (stock.uom),
    // conversions are unreliable. The stock was recorded under a different UOM graph, so we can't calculate
    // correct return quantities. User must delete and re-add the item.
    hasUomBaseMismatch () {
      if (!this.treatmentItem.inventory_enabled) return false
      if (!this.treatmentItem.uom_conversion_enabled) return false
      if (!this.treatmentItem.stock?.uom || !this.treatmentItem.inventory_uom) return false
      return this.treatmentItem.inventory_uom !== this.treatmentItem.stock.uom
    },
    // When the item's selected UOM is no longer in the available conversion options.
    // This happens when UOM conversions were updated after the item was recorded
    // (e.g. admin changed conversion units). Less severe than hasUomBaseMismatch —
    // the item does NOT need deletion, the user just needs to pick a new valid UOM.
    hasUomSelectionMismatch () {
      if (!this.treatmentItem.item_id) return false
      if (!this.hasUomConversion) return false
      if (!this.treatmentItem.selectedUom) return false
      if (this.hasUomBaseMismatch) return false // base mismatch takes priority via its own flow
      return !this.uomOptions.includes(this.treatmentItem.selectedUom)
    },
    uomOptions () {
      const units = []
      if (this.treatmentItem.uom_conversions) {
        this.treatmentItem.uom_conversions.forEach(c => {
          if (c.uom && !units.includes(c.uom)) units.push(c.uom)
          if (c.convert_to && !units.includes(c.convert_to)) units.push(c.convert_to)
        })
      }
      return units
    },
    uomConversionEquivalents () {
      if (!this.hasUomConversion || !this.treatmentItem.quantity) return []
      const graph = buildUomGraph(this.treatmentItem.uom_conversions)
      const selectedScale = getUomScaleFromGraph(graph, this.treatmentItem.selectedUom)
      const baseQty = Number(this.treatmentItem.quantity) * selectedScale
      const sorted = Object.entries(graph).sort((a, b) => b[1] - a[1])
      const baseValue = Math.min(...sorted.map(e => e[1]))
      return sorted.map(([uom, value]) => {
        const uomScale = value / baseValue
        const equiv = baseQty / uomScale
        const display = Number.isInteger(equiv) ? equiv : parseFloat(equiv.toPrecision(4))
        return `${display} ${uom}`
      })
    },
  },
  watch: {
    'treatmentItem.quantity' (newVal, oldVal) {
      if (this.isRevertingChange) return
      this.acknowledgedExcessQty = false
      // Priority 1: Selected UOM no longer in available options — prompt user to pick a new UOM.
      // This is less severe than a base mismatch; the item stays, only UOM needs updating.
      if (this.hasUomSelectionMismatch && oldVal !== undefined) {
        this.pendingChange = { field: 'quantity', oldValue: oldVal }
        this.showUomReselectionDialog = true
        return
      }
      // Priority 2: Base UOM has changed since this stock record was created.
      // Stock return calculation is unreliable — prompt user to delete & re-add.
      if (this.hasUomBaseMismatch && oldVal !== undefined) {
        this.pendingChange = { field: 'quantity', oldValue: oldVal }
        this.showUomMismatchDialog = true
      }
    },
    acknowledgedExcessQty () {
      this.$nextTick(() => {
        if (this.$refs.quantityField) this.$refs.quantityField.validate()
      })
    },
    'treatmentItem.selectedUom' (newVal, oldVal) {
      this.acknowledgedExcessQty = false
      this.$nextTick(() => {
        if (this.$refs.quantityField) this.$refs.quantityField.validate()
      })
      if (this.hasUomConversion && this.hasPriceTier && this.treatmentItem.priceTier) {
        // Edit-loaded tiers lack price_per_uom — refetch so UOM-specific pricing applies.
        if (!('price_per_uom' in this.treatmentItem.priceTier)) {
          this.refetchPriceTierForUom()
        } else if (Array.isArray(this.treatmentItem.priceTier.price_per_uom) && this.treatmentItem.priceTier.price_per_uom.length) {
          this.updateRate(this.treatmentItem.priceTier)
        }
      }
      if (this.isRevertingChange) return
      // Priority 1: Selected UOM no longer in available options — prompt UOM reselection.
      if (this.hasUomSelectionMismatch && oldVal !== undefined) {
        this.pendingChange = { field: 'selectedUom', oldValue: oldVal }
        this.showUomReselectionDialog = true
        return
      }
      // Priority 2: Base UOM mismatch — more severe, requires item deletion.
      if (this.hasUomBaseMismatch && oldVal !== undefined) {
        this.pendingChange = { field: 'selectedUom', oldValue: oldVal }
        this.showUomMismatchDialog = true
      }
    },
  },
  created () {
    this.initialQuantity = this.treatmentItem.quantity || 0
  },
  mounted () {
    this.setData()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      if (this.hasPriceTier) {
        if (!this.treatmentItem.item_id) {
          this.updatePriceTier()
        }
      }
      if (this.isDocument) {
        if (!(this.treatmentItem.document && this.treatmentItem.document.status)) {
          this.showAddMedicalDocumentDialog = true
        }
      }
      if (this.isPackage) {
        if (!this.treatment?.bundle?.available_qty) this.updatePackage()
      }
    },
    updatePriceTier() {
      this.isFetchingPriceTier = true
      let params = { 
        company: this.isHq ? this.hqSelectedCompany?.id : this.company.id,
        product: this.treatmentItem.id
      }
      if (this.registration?.default_payment && this.registration.default_payment.type !== 'self-pay') {
        params.payment_methods = this.registration.default_payment.panel.id
      } else {
        params.payment_channel = 'self-pay'
      }
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          if (resp.data.count) {
            this.$set(this.treatmentItem, 'priceTier', resp.data.results[0])
            this.updateRate(resp.data.results[0])
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPriceTier = false
        })
    },
    refetchPriceTierForUom () {
      this.isFetchingPriceTier = true
      const params = {
        company: this.isHq ? this.hqSelectedCompany?.id : this.company.id,
        product: this.treatmentItem.id
      }
      if (this.registration?.default_payment && this.registration.default_payment.type !== 'self-pay') {
        params.payment_methods = this.registration.default_payment.panel.id
      } else {
        params.payment_channel = 'self-pay'
      }
      const currentTierId = this.treatmentItem.priceTier && this.treatmentItem.priceTier.id
      this.$axios.get('/product_pricetiers', { params })
        .then(resp => {
          if (!resp.data.count) return
          const match = currentTierId ? resp.data.results.find(t => t.id === currentTierId) : resp.data.results[0]
          const tier = match || resp.data.results[0]
          this.$set(this.treatmentItem, 'priceTier', tier)
          this.updateRate(tier)
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPriceTier = false
        })
    },
    updateRate (pricetier) {
      if (!pricetier || !Object.keys(pricetier).length) return
      let price = Number(pricetier.price || 0)
      if (this.hasUomConversion && Array.isArray(pricetier.price_per_uom) && pricetier.price_per_uom.length && this.treatmentItem.selectedUom) {
        const match = pricetier.price_per_uom.find(p => p.uom === this.treatmentItem.selectedUom)
        if (match) price = Number(match.price || 0)
      }
      this.$set(this.treatmentItem, 'rate', price / this.$currency.multiplier)
    },
    updatePackage () {
      this.isFetchingPackage = true
      const params = { products: this.treatmentItem.id }
      const companySlug = this.isHq ? this.hqSelectedCompany.slug : this.company.slug
      this.$axios.get(`/clinics/${companySlug}/packages`, { params })
        .then(resp => {
          if (resp.data.results.length) {
            this.treatmentItem.bundle.available_qty = resp.data.results[0].available_qty
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPackage = false
        })
    },
    updateDocument (data) {
      this.$set(this.treatmentItem, 'document', data)
    },
    duplicateItem () {
      this.$emit('duplicate', this.treatmentItem)
      this.showMessage(['info', 'Treatment item duplicated'])
    },
    openDocument () {
      if (this.allowDocumentEdit) {
        this.showAddMedicalDocumentDialog = true
      } else {
        this.showEditNotAllowedDialog = true
      }
    },
    deleteItem () {
      if (this.hasUomBaseMismatch) {
        this.showSkipInventoryDialog = true
        return
      }
      this.$set(this.treatmentItem, 'delete', true)
      this.showMessage(['neutral600', 'Treatment item removed'])
    },
    onConfirmUomMismatch () {
      this.pendingChange = null
      this.$set(this.treatmentItem, 'skip_inventory_update', true)
      this.$set(this.treatmentItem, 'delete', true)
      this.showMessage(['neutral600', 'Treatment item removed'])
    },
    onConfirmSkipInventory () {
      this.$set(this.treatmentItem, 'skip_inventory_update', true)
      this.$set(this.treatmentItem, 'delete', true)
      this.showMessage(['neutral600', 'Treatment item removed'])
    },
    onCancelSkipInventory () {
      this.showSkipInventoryDialog = false
    },
    onCancelUomMismatch () {
      if (this.pendingChange) {
        // Guard to prevent the revert from re-triggering the watcher
        this.isRevertingChange = true
        this.$set(this.treatmentItem, this.pendingChange.field, this.pendingChange.oldValue)
        this.pendingChange = null
        this.$nextTick(() => { this.isRevertingChange = false })
      }
    },
    // Called when user confirms a new UOM from the reselection dialog.
    // Updates selectedUom to the newly chosen value without deleting the item.
    // Uses isRevertingChange guard to prevent the assignment from re-triggering watchers.
    onConfirmUomReselection (newUom) {
      this.isRevertingChange = true
      this.$set(this.treatmentItem, 'selectedUom', newUom)
      this.pendingChange = null
      this.$nextTick(() => { this.isRevertingChange = false })
    },
    // Called when user cancels the UOM reselection dialog.
    // Reverts the quantity or selectedUom change that originally triggered the dialog.
    onCancelUomReselection () {
      if (this.pendingChange) {
        this.isRevertingChange = true
        this.$set(this.treatmentItem, this.pendingChange.field, this.pendingChange.oldValue)
        this.pendingChange = null
        this.$nextTick(() => { this.isRevertingChange = false })
      }
    },
    deleteChildItem (treatment) {
      this.$set(treatment, 'delete', true)
      this.showMessage(['neutral600', 'Package item removed'])
    },
    checkMedicine (treatment) {
      if (treatment.medication && treatment.medication.id) {
        return true
      }
      return false
    },
    getChildDisplayUom (treatment) {
      if (this.childHasUomConversion(treatment)) {
        return treatment.uom_conversions[treatment.uom_conversions.length - 1].convert_to
      }
      return treatment.uom
    },
    childHasUomConversion (treatment) {
      return treatment.uom_conversion_enabled && treatment.uom_conversions && treatment.uom_conversions.length > 0
    },
    getChildUomConversionEquivalents (treatment) {
      if (!this.childHasUomConversion(treatment) || !treatment.quantity_per_set) return []
      const graph = buildUomGraph(treatment.uom_conversions)
      const selectedScale = getUomScaleFromGraph(graph, treatment.selectedUom)
      const baseQty = Number(treatment.quantity_per_set) * selectedScale
      const sorted = Object.entries(graph).sort((a, b) => b[1] - a[1])
      const baseValue = Math.min(...sorted.map(e => e[1]))
      return sorted.map(([uom, value]) => {
        const uomScale = value / baseValue
        const equiv = baseQty / uomScale
        const display = Number.isInteger(equiv) ? equiv : parseFloat(equiv.toPrecision(4))
        return `${display} ${uom}`
      })
    },
    getChildUomOptions (treatment) {
      const units = []
      if (treatment.uom_conversions) {
        treatment.uom_conversions.forEach(c => {
          if (c.uom && !units.includes(c.uom)) units.push(c.uom)
          if (c.convert_to && !units.includes(c.convert_to)) units.push(c.convert_to)
        })
      }
      return units
    },
    getUomScale (selectedUom) {
      if (!this.treatmentItem.uom_conversions) return 1
      const conversion = this.treatmentItem.uom_conversions.find(c => c.uom === selectedUom || c.convert_to === selectedUom)
      if (!conversion) return 1
      if (conversion.uom === selectedUom) return conversion.scale
      return 1
    },
    getChildInventoryQty (treatment) {
      let qty = Number(treatment.inventory_qty)
      if (treatment.item_id && treatment.stock) {
        qty += Math.abs(treatment.stock.change)
      }
      if (this.childHasUomConversion(treatment) && treatment.selectedUom) {
        const conversion = treatment.uom_conversions?.find(c => c.uom === treatment.selectedUom || c.convert_to === treatment.selectedUom)
        if (conversion && conversion.uom === treatment.selectedUom && conversion.scale > 1) {
          qty = qty / conversion.scale
        }
      }
      return qty
    },
    packageChildrenRules (treatment) {
      if (this.allowOutOfStock) return [v => !!v || 'This field is required', v => v > 0 || 'This field is required']
      const maxQty = treatment.inventory_enabled ? this.getChildInventoryQty(treatment) : null
      return [
        v => !!v || 'This field is required',
        v => v > 0 || 'This field is required',
        v => !treatment.inventory_enabled || Number(v) <= maxQty || 'Item quantity more than available stock'
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .v-text-field--outlined.error--text fieldset {
    border-color: var(--v-danger500) !important;
  }
  .yz-uom-dropdown--sm .v-sheet {
    min-width: 70px !important;
    max-width: 70px !important;
  }
  .custom-height {
    color: var(--v-neutral700);
    height: 32px;
    fieldset {
      height: 37px;
    }
    .v-text-field__slot {
      height: 32px;
      input {
        &:disabled {
          color: var(--v-neutral700);
        }
      }
    }
  }
}
</style>